import React, { useState, useEffect, useRef } from "react";
import "../../../css/liveProgramSlider.css";
import { parseTime, getItalianDay } from "../../../utils/dateHelpers";

import LiveProgramCard       from "./LiveProgramCard";
import TimelineDaily         from "./TimelineDaily";
import ScheduleModal         from "./ScheduleModal";

const LiveProgramSlider = ({ programs, schedules }) => {
  /* ------------------------------------------------------------------ */
  /* STATE                                                               */
  /* ------------------------------------------------------------------ */
  const [currentProgram,    setCurrentProgram] = useState(null);
  const [progress,          setProgress]       = useState(0);

  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [selectedDate,      setSelectedDate]      = useState(new Date());

  const [isLiveExpanded,    setIsLiveExpanded]    = useState(false);
  const [scheduleModal,     setScheduleModal]     = useState(null);

  const prevLiveProgramRef = useRef({ currentProgram: null, progress: 0 });

  // Normalizza qualsiasi formato data a YYYY-MM-DD
  const toYMD = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      return value.includes('T') ? value.slice(0, 10) : value;
    }
    if (value instanceof Date) {
      return value.toISOString().slice(0, 10);
    }
    return '';
  };

  const resolveSlotsForDate = (schedule, targetDate) => {
    if (!schedule) return { slots: [], occurrence: null };
    const occurrences = schedule.occurrences || [];
    const baseSlots = schedule.timeSlots || [];
    if (!targetDate) return { slots: baseSlots, occurrence: null };
    const key = toYMD(targetDate);
    const match = occurrences.find(occ => toYMD(occ.date) === key);
    const slots = match?.timeSlots?.length ? match.timeSlots : baseSlots;
    return { slots, occurrence: match || null };
  };

  /* ------------------------------------------------------------------ */
  /* CALLBACKS                                                           */
  /* ------------------------------------------------------------------ */
  const handleDayChange      = date      => setSelectedDate(date);
  const toggleLiveExpansion  = ()        => setIsLiveExpanded(p => !p);

  const handleTimelineSelect = sch => {
    // Apri direttamente il modal con i dati già risolti da TimelineDaily
    setScheduleModal(sch);
  };

  /* ------------------------------------------------------------------ */
  /* LIVE / CURRENT PROGRAM LOGIC                                       */
  /* Cerca prima in live oggi, poi upcoming oggi, poi nei prossimi 7 gg */
  /* ------------------------------------------------------------------ */
  const updateProgramStatus = () => {
    if (!schedules || !programs) return;

    const now = new Date();
    let liveSchedule     = null;
    let upcomingSchedule = null;
    let upcomingDate     = null; // Data in cui si verifica l'upcoming

    // Helper: verifica se una data è nella blacklist
    const isDateBlacklisted = (sch, date) => {
      if (!sch.blacklistDates || !Array.isArray(sch.blacklistDates)) return false;
      const dateStr = toYMD(date);
      return sch.blacklistDates.some(b => {
        const blacklistDate = b.blacklistDate || b.blacklist_date || b;
        return toYMD(blacklistDate) === dateStr;
      });
    };

    // Helper: filtra schedule per una data specifica
    const getSchedulesForDate = (date) => {
      return schedules.filter(sch => {
        if (sch.isRecurring) {
          // Verifica che sia il giorno giusto E che non sia in blacklist
          const isRightDay = sch.dayOfWeek &&
                 sch.dayOfWeek.toLowerCase() === getItalianDay(date).toLowerCase();
          return isRightDay && !isDateBlacklisted(sch, date);
        }
        if (sch.date) {
          return new Date(sch.date).toDateString() === date.toDateString();
        }
        return false;
      }).sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));
    };

    // 1. Cerca prima tra gli schedule di oggi
    const todaysSchedules = getSchedulesForDate(now);

    for (const sch of todaysSchedules) {
      const start = parseTime(sch.startTime);
      const end   = parseTime(sch.endTime);
      if (now >= start && now <= end) {
        liveSchedule = sch;
        break;
      } else if (now < start && !upcomingSchedule) {
        upcomingSchedule = sch;
        upcomingDate = now;
      }
    }

    // 2. Se non ho trovato nulla oggi, cerca nei prossimi 7 giorni
    if (!liveSchedule && !upcomingSchedule) {
      for (let i = 1; i <= 7; i++) {
        const futureDate = new Date(now);
        futureDate.setDate(futureDate.getDate() + i);
        const futureSchedules = getSchedulesForDate(futureDate);
        if (futureSchedules.length > 0) {
          upcomingSchedule = futureSchedules[0];
          upcomingDate = futureDate;
          break;
        }
      }
    }

    let newCurrentProgram = null;
    let newProgress       = 0;

    if (liveSchedule) {
      const prog = programs.find(p => Number(p.id) === Number(liveSchedule.programId));
      if (prog) {
        liveSchedule.programData = prog;
        const total    = parseTime(liveSchedule.endTime) - parseTime(liveSchedule.startTime);
        const elapsed  = now - parseTime(liveSchedule.startTime);
        newCurrentProgram = { program: prog, schedule: liveSchedule, isLive: true };
        newProgress       = Math.min(100, Math.max(0, (elapsed / total) * 100));
        const resolved = resolveSlotsForDate(liveSchedule, now);
        liveSchedule.activeSlots = resolved.slots;
        if (resolved.occurrence) {
          liveSchedule.activeOccurrence = resolved.occurrence;
        }
      }
    } else if (upcomingSchedule) {
      const prog = programs.find(p => Number(p.id) === Number(upcomingSchedule.programId));
      if (prog) {
        upcomingSchedule.programData = prog;
        newCurrentProgram = { program: prog, schedule: upcomingSchedule, isLive: false, scheduledDate: upcomingDate };
        newProgress       = 0;
        // Usa upcomingDate per risolvere l'occorrenza corretta
        const resolved = resolveSlotsForDate(upcomingSchedule, upcomingDate);
        upcomingSchedule.activeSlots = resolved.slots;
        if (resolved.occurrence) {
          upcomingSchedule.activeOccurrence = resolved.occurrence;
        }
      }
    }

    const old           = prevLiveProgramRef.current;
    const isSameProg    = old.currentProgram?.schedule?.id === newCurrentProgram?.schedule?.id &&
                          old.currentProgram?.isLive        === newCurrentProgram?.isLive;
    const isSameProgress = Math.floor(old.progress) === Math.floor(newProgress);

    if (!isSameProg || !isSameProgress) {
      setCurrentProgram(newCurrentProgram);
      setProgress(newProgress);
      prevLiveProgramRef.current = { currentProgram: newCurrentProgram, progress: newProgress };
    }
  };

  useEffect(() => {
    updateProgramStatus();
    const id = setInterval(updateProgramStatus, 1_000);
    return () => clearInterval(id);
  }, [schedules, programs]);

  /* ------------------------------------------------------------------ */
  /* UPCOMING SCHEDULES — RICALCOLO IN BASE ALLA DATA SELEZIONATA       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!schedules || !schedules.length) { setUpcomingSchedules([]); return; }

    const dayName = getItalianDay(selectedDate).toLowerCase();

    // Helper: verifica se una data è nella blacklist
    const isDateBlacklistedForGrid = (sch, date) => {
      if (!sch.blacklistDates || !Array.isArray(sch.blacklistDates)) return false;
      const dateStr = toYMD(date);
      return sch.blacklistDates.some(b => {
        const blacklistDate = b.blacklistDate || b.blacklist_date || b;
        return toYMD(blacklistDate) === dateStr;
      });
    };

    const list = schedules
      .filter(sch => {
        if (sch.isRecurring && sch.dayOfWeek) {
          return sch.dayOfWeek.toLowerCase() === dayName &&
                 !isDateBlacklistedForGrid(sch, selectedDate);
        }
        if (sch.date) {
          return new Date(sch.date).toDateString() === selectedDate.toDateString();
        }
        return false;
      })
      .map(sch => {
        const enriched = {
          ...sch,
          programData: programs.find(p => Number(p.id) === Number(sch.programId)),
        };
        const resolved = resolveSlotsForDate(enriched, selectedDate);
        enriched.activeSlots = resolved.slots;
        if (resolved.occurrence) {
          enriched.activeOccurrence = resolved.occurrence;
        }
        return enriched;
      })
      .sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));

    setUpcomingSchedules(list);
  }, [schedules, programs, selectedDate]);

  /* ------------------------------------------------------------------ */
  /* FORMATTERS                                                         */
  /* ------------------------------------------------------------------ */
  const formatScheduleTime = sch => {
    const now = new Date();
    let scheduleDate;

    if (!sch.isRecurring && sch.date) {
      scheduleDate = new Date(`${sch.date}T${sch.startTime}:00`);
    } else if (sch.isRecurring && sch.dayOfWeek) {
      const dayMap = {
        lunedì: 1, martedì: 2, mercoledì: 3, giovedì: 4,
        venerdì: 5, sabato: 6, domenica: 0,
      };
      scheduleDate = new Date();
      let diff = dayMap[sch.dayOfWeek.toLowerCase()] - scheduleDate.getDay();
      if (diff <= 0) diff += 7;
      scheduleDate.setDate(scheduleDate.getDate() + diff);
      const [h, m] = sch.startTime.split(":").map(Number);
      scheduleDate.setHours(h, m, 0, 0);
    }

    if (scheduleDate && scheduleDate.toDateString() === now.toDateString()) {
      return `${sch.startTime} - ${sch.endTime}`;
    }
    return scheduleDate
      ? `${scheduleDate.toLocaleDateString()} ${sch.startTime} - ${sch.endTime}`
      : `${sch.startTime} - ${sch.endTime}`;
  };

  /* ------------------------------------------------------------------ */
  /* RENDER                                                             */
  /* ------------------------------------------------------------------ */
  const isViewingToday = selectedDate.toDateString() === new Date().toDateString();
  const dayLabel = selectedDate.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <>
      <TimelineDaily
        schedules={schedules}
        programs={programs}
        now={new Date()}
        onSelect={handleTimelineSelect}
        onDayChange={handleDayChange}
      />

      <div className="live-program-slider-container">
        {/* === SEZIONE LIVE / PROSSIMAMENTE === */}
        {currentProgram && (
          <LiveProgramCard
            data={currentProgram}
            progress={progress}
            expanded={isLiveExpanded}
            onToggleExpanded={toggleLiveExpansion}
            onOpenSchedule={() => setScheduleModal({
              ...currentProgram.schedule,
              programData: currentProgram.schedule.programData || currentProgram.program,
            })}
          />
        )}

        {/* === SEZIONE PROGRAMMI DEL GIORNO SELEZIONATO === */}
        {upcomingSchedules.length > 0 && (
          <>
            <div className="day-schedules-section">
              <h3 className="day-schedules-title">
                <i className="fa fa-calendar-o" style={{ marginRight: 8 }} />
                Programmi di {isViewingToday ? "oggi" : dayLabel}
              </h3>
              <div className="day-schedules-grid">
                {upcomingSchedules.map(sch => {
                  const schId = sch.id || `${sch.programId}-${sch.startTime}-${sch.date || ""}`;
                  const prog = sch.programData || {};
                  return (
                    <div
                      key={schId}
                      className="day-schedule-card"
                      onClick={() => setScheduleModal({
                        ...sch,
                        programData: prog,
                      })}
                    >
                      <div
                        className="day-schedule-card-image"
                        style={{
                          backgroundImage: `url(${prog.imageUrl || prog.image_url || "https://via.placeholder.com/100"})`,
                        }}
                      />
                      <div className="day-schedule-card-info">
                        <span className="day-schedule-time">
                          {(sch.startTime || "").slice(0, 5)} - {(sch.endTime || "").slice(0, 5)}
                        </span>
                        <h4 className="day-schedule-title">{prog.title || "Programma"}</h4>
                        <span className="day-schedule-priority">P{sch.priority ?? 0}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {!currentProgram && upcomingSchedules.length === 0 && (
          <h2 style={{ color: "#fff", textAlign: "center" }}>
            Nessun programma per {dayLabel}.
          </h2>
        )}
      </div>
      {scheduleModal && (
        <ScheduleModal
          sch={scheduleModal}
          onClose={() => setScheduleModal(null)}
        />
      )}
    </>
  );
};

export default LiveProgramSlider;
